<script setup lang="ts">
/**
 * CookieConsent Banner
 *
 * GDPR-compliant cookie consent banner shown on first visit.
 * Persists consent in localStorage to avoid repeat prompts.
 */
import { ref, onMounted } from 'vue'

const CONSENT_KEY = 'rws-cookie-consent'

interface ConsentRecord {
    accepted: boolean
    timestamp: string
}

const visible = ref(false)

onMounted(() => {
    const stored = localStorage.getItem(CONSENT_KEY)
    if (!stored) {
        visible.value = true
    }
})

function accept(all: boolean) {
    const record: ConsentRecord = {
        accepted: all,
        timestamp: new Date().toISOString(),
    }
    localStorage.setItem(CONSENT_KEY, JSON.stringify(record))
    visible.value = false
}
</script>

<template>
    <Transition name="consent-slide">
        <div v-if="visible" class="consent-banner" role="dialog" aria-label="Cookie consent">
            <div class="consent-inner">
                <div class="consent-text">
                    <svg class="consent-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <path d="M12 15v.01M12 12a1.5 1.5 0 10-1.14-2.47A4 4 0 003 12a9 9 0 0018 0c0-.71-.08-1.4-.24-2.06A3 3 0 0018 12a3 3 0 01-6 0z" />
                    </svg>
                    <p>
                        We use cookies and local storage for authentication, preferences, and analytics.
                        Your data is never sold to third parties.
                        <router-link to="/privacy" class="consent-link">Privacy Policy</router-link>
                    </p>
                </div>
                <div class="consent-actions">
                    <button class="consent-btn consent-btn--secondary" @click="accept(false)">
                        Necessary Only
                    </button>
                    <button class="consent-btn consent-btn--primary" @click="accept(true)">
                        Accept All
                    </button>
                </div>
            </div>
        </div>
    </Transition>
</template>

<style scoped>
.consent-banner {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 9999;
    padding: 0.875rem 1.25rem;
    background: rgba(15, 15, 25, 0.92);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.3);
}

.consent-inner {
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.25rem;
}

.consent-text {
    display: flex;
    align-items: flex-start;
    gap: 0.625rem;
    flex: 1;
}

.consent-icon {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    color: rgba(18, 192, 221, 0.7);
    margin-top: 1px;
}

.consent-text p {
    font-size: 0.8125rem;
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.7);
    margin: 0;
}

.consent-link {
    color: #12c0dd;
    text-decoration: none;
    font-weight: 500;
    transition: color 0.15s;
}

.consent-link:hover {
    color: #5ee8ff;
    text-decoration: underline;
}

.consent-actions {
    display: flex;
    gap: 0.5rem;
    flex-shrink: 0;
}

.consent-btn {
    padding: 0.4375rem 0.875rem;
    border-radius: 6px;
    font-size: 0.8125rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s;
    white-space: nowrap;
    border: none;
}

.consent-btn--primary {
    background: linear-gradient(135deg, #12c0dd, #8b5cf6);
    color: #fff;
}

.consent-btn--primary:hover {
    filter: brightness(1.1);
    transform: translateY(-1px);
}

.consent-btn--secondary {
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.7);
    border: 1px solid rgba(255, 255, 255, 0.12);
}

.consent-btn--secondary:hover {
    background: rgba(255, 255, 255, 0.14);
    color: #fff;
}

/* Slide transition */
.consent-slide-enter-active {
    transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s;
}

.consent-slide-leave-active {
    transition: transform 0.3s ease-in, opacity 0.2s;
}

.consent-slide-enter-from {
    transform: translateY(100%);
    opacity: 0;
}

.consent-slide-leave-to {
    transform: translateY(100%);
    opacity: 0;
}

/* Responsive */
@media (max-width: 640px) {
    .consent-inner {
        flex-direction: column;
        align-items: stretch;
    }

    .consent-actions {
        justify-content: flex-end;
    }
}
</style>
